import { useMemo } from 'react';
import { FlaskConical, RefreshCw } from 'lucide-react';

import { HelpTooltip } from '@/components/ui/help';
import type { ChannelOption } from '@/types/broadcastQueue';

export type TestSelfMode = 'snapshot' | 'deep_emulation' | 'channel_matrix';

interface TestSelfResultItemLike {
  success: boolean;
  message: string;
  error?: string;
  clone_item_id?: string;
  target_label?: string;
  target_value?: string;
  target_channel?: string;
}

export interface TestSelfResultLike {
  success: boolean;
  message: string;
  error?: string;
  target_label?: string;
  target_value?: string;
  results?: TestSelfResultItemLike[];
}

function getTargetHint(channel: string, mode: TestSelfMode): string {
  if (mode === 'channel_matrix') {
    return 'Система заново подберёт channel-specific шаблоны и отправит вам тестовые копии по выбранным каналам через тот же runtime flow, что и для пациента.';
  }
  if (mode === 'deep_emulation') {
    return 'Проверка как перед реальной отправкой: сообщение соберётся заново и придёт на ваш тестовый контакт.';
  }

  switch (channel) {
    case 'telegram_bot':
    case 'telegram_client':
      return 'Отправим вам проверочную копию в ваш подтверждённый Telegram для этой клиники.';
    case 'max':
      return 'Отправим вам проверочную копию в вашу подтверждённую MAX-привязку для этой клиники.';
    case 'sms':
    case 'whatsapp_waba':
    case 'whatsapp_regular':
      return 'Отправим проверочную копию на ваш тестовый номер +7 926 676-44-95.';
    default:
      return 'Создадим проверочную копию и отправим её на ваш тестовый контакт.';
  }
}

interface TestSelfSectionProps {
  currentChannel: string;
  mode: TestSelfMode;
  onModeChange: (mode: TestSelfMode) => void;
  onSend: () => void | Promise<void>;
  allowChannelMatrix?: boolean;
  availableChannels?: ChannelOption[];
  selectedChannels: string[];
  onSelectedChannelsChange: (channels: string[]) => void;
  isSending: boolean;
  error?: string | null;
  result?: TestSelfResultLike | null;
}

export function TestSelfSection({
  currentChannel,
  mode,
  onModeChange,
  onSend,
  allowChannelMatrix = true,
  availableChannels = [],
  selectedChannels,
  onSelectedChannelsChange,
  isSending,
  error,
  result,
}: TestSelfSectionProps) {
  const hint = useMemo(() => getTargetHint(currentChannel, mode), [currentChannel, mode]);
  const selectableChannels = useMemo(
    () => availableChannels.filter((channel) => channel.available && channel.test_available),
    [availableChannels]
  );

  const toggleChannel = (channel: string) => {
    if (selectedChannels.includes(channel)) {
      onSelectedChannelsChange(selectedChannels.filter((value) => value !== channel));
      return;
    }
    onSelectedChannelsChange([...selectedChannels, channel]);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-neutral-700">
        <FlaskConical className="h-4 w-4" />
        <span>Тест себе</span>
        <HelpTooltip
          side="right"
          maxWidth="320px"
          content="Первый режим отправляет вам сообщение ровно в том виде, как оно сейчас подготовлено. Второй режим собирает сообщение заново и удобен для проверки ссылок, кнопок и подстановки данных."
        />
      </div>

      <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-xs leading-5 text-violet-800">
        {hint}
      </div>

      <div className={`grid grid-cols-1 gap-2 ${allowChannelMatrix ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        <button
          type="button"
          onClick={() => onModeChange('snapshot')}
          className={`rounded-md border px-3 py-2 text-left text-xs transition-colors ${
            mode === 'snapshot'
              ? 'border-violet-400 bg-violet-100 text-violet-900'
              : 'border-neutral-200 bg-white text-neutral-600 hover:border-violet-200'
          }`}
        >
          <div className="font-medium">Как сейчас в очереди</div>
          <div>Отправим вам сообщение ровно в том виде, как оно сейчас подготовлено.</div>
        </button>

        <button
          type="button"
          onClick={() => onModeChange('deep_emulation')}
          className={`rounded-md border px-3 py-2 text-left text-xs transition-colors ${
            mode === 'deep_emulation'
              ? 'border-violet-400 bg-violet-100 text-violet-900'
              : 'border-neutral-200 bg-white text-neutral-600 hover:border-violet-200'
          }`}
        >
          <div className="font-medium">Пересобрать перед отправкой</div>
          <div>Система заново соберёт сообщение, чтобы проверить ссылки, кнопки и подстановки данных.</div>
        </button>

        {allowChannelMatrix && (
          <button
            type="button"
            onClick={() => onModeChange('channel_matrix')}
            className={`rounded-md border px-3 py-2 text-left text-xs transition-colors ${
              mode === 'channel_matrix'
                ? 'border-violet-400 bg-violet-100 text-violet-900'
                : 'border-neutral-200 bg-white text-neutral-600 hover:border-violet-200'
            }`}
          >
            <div className="font-medium">Полная пересборка по каналам</div>
            <div>Подберём channel-specific шаблоны и отправим тестовые копии по выбранным каналам.</div>
          </button>
        )}
      </div>

      {allowChannelMatrix && mode === 'channel_matrix' && (
        <div className="space-y-3 rounded-lg border border-violet-200 bg-violet-50 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-xs font-medium text-violet-900">Выберите каналы для тестовой пересборки</div>
            <button
              type="button"
              onClick={() => onSelectedChannelsChange(selectableChannels.map((channel) => channel.channel))}
              className="text-xs text-violet-700 underline"
            >
              Выбрать все доступные
            </button>
            <button
              type="button"
              onClick={() => onSelectedChannelsChange([])}
              className="text-xs text-violet-700 underline"
            >
              Снять выбор
            </button>
          </div>

          <div className="space-y-2">
            {availableChannels.map((channel) => {
              const canSelect = channel.available && channel.test_available;
              return (
                <label
                  key={channel.channel}
                  className={`flex items-start gap-3 rounded-md border px-3 py-2 text-xs ${
                    canSelect ? 'border-violet-200 bg-white text-neutral-700' : 'border-neutral-200 bg-neutral-50 text-neutral-400'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={selectedChannels.includes(channel.channel)}
                    disabled={!canSelect || isSending}
                    onChange={() => toggleChannel(channel.channel)}
                  />
                  <div className="space-y-1">
                    <div className="font-medium">{channel.display_name}</div>
                    {channel.test_target_label && channel.test_target_value && (
                      <div className="text-neutral-500">
                        Тестовый контакт: {channel.test_target_label} {channel.test_target_value}
                      </div>
                    )}
                    {!canSelect && (
                      <div className="text-red-500">{channel.reason || channel.test_reason || 'Канал недоступен для тестовой пересборки'}</div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => void onSend()}
        disabled={isSending || !currentChannel || (mode === 'channel_matrix' && selectedChannels.length === 0)}
        className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-violet-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:bg-violet-300"
      >
        {isSending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}
        {isSending ? 'Отправляем тестовую копию...' : 'Отправить тест себе'}
      </button>

      <div className="text-xs text-neutral-500">
        {mode === 'snapshot'
          ? 'Сообщение придёт вам ровно в том виде, в каком оно сейчас подготовлено для пациента.'
          : mode === 'deep_emulation'
            ? 'Сообщение соберётся заново, как перед реальной отправкой пациенту. Это удобно для проверки ссылок, кнопок и подстановки данных.'
            : 'Для каждого выбранного канала будет создана отдельная тестовая копия с тем шаблоном и тем рендером, которые реально использует event-driven pipeline.'}
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}

      {result && (
        <div
          className={`rounded-md border px-3 py-2 text-xs ${
            result.success ? 'border-green-200 bg-green-50 text-green-700' : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}
        >
          <div>
            {result.message}
            {result.target_label && result.target_value ? ` (${result.target_label}: ${result.target_value})` : ''}
          </div>
          {result.results && result.results.length > 1 && (
            <div className="mt-2 space-y-1">
              {result.results.map((item) => (
                <div key={`${item.target_channel}-${item.clone_item_id || item.message}`}>
                  <span className="font-medium">{item.target_channel || 'channel'}:</span> {item.message}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
